import type { L10n } from '@/i18n'

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  SITE CONFIG — EVERYTHING "BRAND" ABOUT THIS WEBSITE LIVES HERE          ║
 * ╠══════════════════════════════════════════════════════════════════════════╣
 * ║  Selling something other than watches? Three files, no components:       ║
 * ║    1. this file             → name, copy, addresses, imagery, currency   ║
 * ║    2. src/data/seed.ts      → the demo rows of the database              ║
 * ║    3. src/styles/globals.css → the light & dark palette                  ║
 * ║                                                                          ║
 * ║  Text here is bilingual — written as { en, vi } and read with `tx()`     ║
 * ║  from useI18n(). Interface wording lives in src/i18n/en.ts | vi.ts.      ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

const l = (en: string, vi: string): L10n => ({ en, vi })

export const siteConfig = {
  // ── Identity ─────────────────────────────────────────────────────────────
  name: 'TEMPORA',
  legalName: l('Tempora Horlogerie SA', 'Công ty Đồng hồ Tempora'),
  tagline: l('Maison Horlogère · since 1948', 'Nhà chế tác đồng hồ · từ 1948'),
  description: l(
    'Fine mechanical watches, inspected and guaranteed by our own watchmakers.',
    'Đồng hồ cơ tinh tuyển, được chính thợ của nhà kiểm định và bảo chứng.',
  ),

  /** ISO 4217 code. Prices in seed.ts and in the studio are entered in this. */
  currency: 'USD',

  // ── Home page hero ───────────────────────────────────────────────────────
  hero: {
    image:
      'https://images.unsplash.com/photo-1612817159949-195b6eb9e31a?auto=format&fit=crop&w=1800&q=80',
    imageAlt: l('A steel dive watch on a dark ground', 'Chiếc đồng hồ lặn vỏ thép trên nền tối'),
  },

  /** The quiet band of promises that scrolls under the hero. */
  marquee: ['marqueeService', 'marqueeAuthenticity', 'marqueeShipping', 'marqueeReturns'],

  // ── Editorial imagery (the atelier / visit bands) ─────────────────────────
  story: {
    image:
      'https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?auto=format&fit=crop&w=1400&q=80',
    imageAlt: l('A pocket watch from the archive', 'Chiếc đồng hồ quả quýt trong kho lưu trữ'),
  },
  visit: {
    image:
      'https://images.unsplash.com/photo-1495856458515-0637185db551?auto=format&fit=crop&w=1600&q=80',
    imageAlt: l('A watch resting on stone', 'Chiếc đồng hồ đặt trên phiến đá'),
  },

  /** Chapter imagery for /maison, in the order the chapters appear. */
  maisonImages: [
    'https://images.unsplash.com/photo-1596460107916-430662021049?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1508057198894-247b23fe5ade?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1620625515032-6ed0c1790c75?auto=format&fit=crop&w=1200&q=80',
  ],

  /** Numbers on the /maison page. Plain strings — they are typography, not maths. */
  maisonStats: [
    { value: '76', labelKey: 'maison.statYears' },
    { value: '4,200+', labelKey: 'maison.statPieces' },
    { value: '31', labelKey: 'maison.statCountries' },
    { value: '∞', labelKey: 'maison.statService' },
  ],

  /** The three promises on the home page. `icon` is a name from src/lib/icons.ts. */
  services: [
    { icon: 'BadgeCheck', titleKey: 'home.service1Title', bodyKey: 'home.service1Body' },
    { icon: 'Wrench', titleKey: 'home.service2Title', bodyKey: 'home.service2Body' },
    { icon: 'Package', titleKey: 'home.service3Title', bodyKey: 'home.service3Body' },
  ],

  // ── Salons ───────────────────────────────────────────────────────────────
  salons: [
    {
      city: l('Hanoi', 'Hà Nội'),
      address: l('18 Ly Thai To, Hoan Kiem', '18 Lý Thái Tổ, Hoàn Kiếm'),
      phone: '+84 24 3826 1948',
    },
    {
      city: l('Geneva', 'Genève'),
      address: l('7 Rue du Rhône, 1204', '7 Rue du Rhône, 1204'),
      phone: '+41 22 311 1948',
    },
  ],

  contact: {
    email: 'salon@tempora.watch',
    phone: '+84 24 3826 1948',
  },

  /** Footer social links. `icon` is a name from src/lib/icons.ts. */
  socials: [
    { icon: 'Instagram', label: 'Instagram', href: 'https://instagram.com' },
    { icon: 'Facebook', label: 'Facebook', href: 'https://facebook.com' },
    { icon: 'Youtube', label: 'YouTube', href: 'https://youtube.com' },
  ],

  /**
   * Header navigation. `key` is looked up in the `nav` section of the
   * dictionaries, so adding a language translates the menu for free.
   */
  nav: [
    { key: 'home', to: '/' },
    { key: 'collection', to: '/collection' },
    { key: 'maison', to: '/maison' },
    { key: 'contact', to: '/contact' },
  ],
}

export type SiteConfig = typeof siteConfig
